"use client";

import {
  CalendarDays,
  Compass,
  MessageCircle,
  Plus,
  UserRound,
  FileText,
  X,
  Search,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Brand } from "./brand";
import { LocationControls } from "./location-context";

const items = [
  { href: "/", label: "Browse", icon: Compass },
  { href: "/dates", label: "Dates", icon: CalendarDays },
  { href: "#create", label: "Post", icon: Plus, create: true },
  { href: "/messages", label: "Messages", icon: MessageCircle },
  { href: "/profile", label: "Account", icon: UserRound },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const path = useLocation().pathname;
  const [createOpen, setCreateOpen] = useState(false);
  const [online, setOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine,
  );
  const [updateApp, setUpdateApp] = useState<null | (() => Promise<void>)>(
    null,
  );

  useEffect(() => {
    if (!createOpen) return;
    const close = (event: KeyboardEvent) =>
      event.key === "Escape" && setCreateOpen(false);
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [createOpen]);

  useEffect(() => {
    const connected = () => setOnline(true);
    const disconnected = () => setOnline(false);
    const update = (event: Event) =>
      setUpdateApp(() => (event as CustomEvent<() => Promise<void>>).detail);
    window.addEventListener("online", connected);
    window.addEventListener("offline", disconnected);
    window.addEventListener("krosspaths-update", update);
    return () => {
      window.removeEventListener("online", connected);
      window.removeEventListener("offline", disconnected);
      window.removeEventListener("krosspaths-update", update);
    };
  }, []);

  const nav = items.map(({ href, label, icon: Icon, create }) =>
    create ? (
      <button
        key={label}
        className="nav-link"
        type="button"
        onClick={() => setCreateOpen(true)}
        aria-label="Create a Post or Date"
      >
        <span className="create-nav">
          <Icon size={24} strokeWidth={2.25} />
        </span>
        <span>{label}</span>
      </button>
    ) : (
      <Link
        key={label}
        className={`nav-link ${path === href ? "active" : ""}`}
        to={href}
      >
        <Icon size={21} fill={path === href ? "currentColor" : "none"} />
        <span>{label}</span>
      </Link>
    ),
  );

  return (
    <div className="app-frame">
      <header className="site-header">
        <div className="site-header-main">
          <Link to="/" aria-label="Krosspaths home">
            <Brand />
          </Link>
          <LocationControls compact />
          <label className="header-search">
            <Search size={18} />
            <span className="sr-only">Search posts and dates</span>
            <input type="search" placeholder="Search posts and dates" />
          </label>
          <button
            className="button button-primary desktop-post-button"
            type="button"
            onClick={() => setCreateOpen(true)}
          >
            Post an ad
          </button>
          <Link className="header-action" to="/messages">
            <MessageCircle size={20} />
            <span>Messages</span>
          </Link>
          <Link className="header-action" to="/profile">
            <UserRound size={20} />
            <span>Account</span>
          </Link>
        </div>
        <nav className="desktop-categories" aria-label="Browse categories">
          <Link to="/">All Posts</Link>
          <Link to="/posts">All Posts</Link>
          <Link to="/posts?category=NSA">NSA</Link>
          <Link to="/posts?category=Friendship">Friendship</Link>
          <Link to="/posts?category=Drinking%20pals">Drinking pals</Link>
          <Link to="/posts?category=Dating">Dating</Link>
          <Link to="/posts?category=Events">Events</Link>
          <Link to="/dates">Dates</Link>
        </nav>
      </header>
      {children}
      <nav className="mobile-nav" aria-label="Primary navigation">
        {nav}
      </nav>
      {!online && (
        <div className="status-toast" role="status">
          You’re offline. Drafts remain saved on this device.
        </div>
      )}
      {updateApp && (
        <button
          className="status-toast update-toast"
          onClick={() => updateApp()}
        >
          A Krosspaths update is ready · Refresh
        </button>
      )}
      {createOpen && (
        <div
          className="sheet-backdrop"
          role="presentation"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setCreateOpen(false)
          }
        >
          <section
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-heading"
          >
            <div className="sheet-handle" />
            <div className="page-header">
              <div>
                <p className="eyebrow">Make something happen</p>
                <h2 className="section-title" id="create-heading">
                  What are you planning?
                </h2>
              </div>
              <button
                className="icon-button"
                type="button"
                onClick={() => setCreateOpen(false)}
                aria-label="Close create menu"
              >
                <X size={19} />
              </button>
            </div>
            <div className="create-options">
              <Link
                className="create-option"
                to="/create/post"
                onClick={() => setCreateOpen(false)}
              >
                <FileText size={25} />
                <strong>Post</strong>
                <span className="muted">Share what you’re looking for</span>
              </Link>
              <Link
                className="create-option"
                to="/create/date"
                onClick={() => setCreateOpen(false)}
              >
                <CalendarDays size={25} />
                <strong>Date</strong>
                <span className="muted">Set a time and place</span>
              </Link>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
